import { useState, useRef, useEffect } from 'react';
import { Building2, ChevronDown, Plus, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

const MOCK_ORGS = [
  { id: 'org-1', name: 'ACME Corp', role: 'owner', initial: 'A' },
  { id: 'org-2', name: 'Globex Inc', role: 'admin', initial: 'G' },
  { id: 'org-3', name: 'Initech', role: 'member', initial: 'I' },
];

export function OrgSwitcher() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(MOCK_ORGS[0]);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-sidebar-accent transition-colors text-[13px]"
      >
        <div className="h-6 w-6 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center text-[10px] font-bold text-primary">
          {selected.initial}
        </div>
        <span className="text-sidebar-foreground/80 font-medium hidden md:inline max-w-[100px] truncate">{selected.name}</span>
        <ChevronDown className="h-3 w-3 text-sidebar-foreground/40" />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1.5 w-56 bg-popover border border-border rounded-xl shadow-xl shadow-black/10 py-1.5 z-50">
          <p className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Organizations</p>
          {MOCK_ORGS.map(org => (
            <button
              key={org.id}
              onClick={() => { setSelected(org); setOpen(false); }}
              className={cn(
                "flex items-center gap-2.5 w-full px-3 py-2 text-[13px] hover:bg-accent transition-colors",
                selected.id === org.id && "bg-accent"
              )}
            >
              <div className="h-6 w-6 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center text-[10px] font-bold text-primary">
                {org.initial}
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className="text-[12px] font-medium text-popover-foreground truncate">{org.name}</p>
                <p className="text-[10px] text-muted-foreground capitalize">{org.role}</p>
              </div>
              {selected.id === org.id && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
            </button>
          ))}
          <div className="h-px bg-border my-1.5" />
          <button className="flex items-center gap-2.5 w-full px-3 py-2 text-[12px] text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">
            <Plus className="h-3.5 w-3.5" /> Create organization
          </button>
        </div>
      )}
    </div>
  );
}
